// The multi-lead office follow-up report (build item 16), matching
// docs/design/Lead Manager-print.dc.html: header rule in accent, "THE
// PARADISE — TRADESHOW LEAD REPORT", event scope + generation note, a
// summary row (Open Leads / Pipeline Value / Advance Received), then one
// break-inside-avoid block per lead. Filterable by event and stage — the
// filter controls hide in `@media print` (Tailwind's `print:hidden`).
// hideShellChrome — this is a print view, full-bleed, no app shell.
import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';

import { useProductItemNoLookup, useProductMeasureLookup } from '@/components/lead-detail/use-product-covers';
import { LeadReportBlock } from '@/components/print/lead-report-block';
import { PrintFooter, PrintHeader } from '@/components/print/print-header';
import '@/components/print/print.css';
import { type EventFilterValue, ReportFilters, type StageFilterValue } from '@/components/print/report-filters';
import { computeReportSummary } from '@/components/print/report-summary';
import { ReportSummaryRow } from '@/components/print/report-summary-row';
import { useLeadsReportQuery } from '@/components/print/use-leads-report-query';
import { Empty, EmptyDescription, EmptyHeader } from '@/components/ui/empty';
import { useEventsQuery } from '@/lib/events/use-events-query';

export const Route = createFileRoute('/_app/reports/leads')({
  staticData: { hideShellChrome: true },
  component: ReportsLeadsRoute
});

const STATUS_MESSAGE: Record<'offline' | 'unauthenticated' | 'error', string> = {
  offline: 'Offline — this report needs a connection to load leads.',
  unauthenticated: 'Signed out — sign in again to load leads.',
  error: 'Could not load leads. Try again in a moment.'
};

function ReportsLeadsRoute() {
  const [eventFilter, setEventFilter] = useState<EventFilterValue>('all');
  const [stageFilter, setStageFilter] = useState<StageFilterValue>('all');

  const { data: events } = useEventsQuery();
  const { data: leadsResult, isPending } = useLeadsReportQuery();
  const measureByProductId = useProductMeasureLookup();
  const itemNoByProductId = useProductItemNoLookup();

  const eventNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const event of events ?? []) map.set(event.id, event.name);
    return map;
  }, [events]);

  const filteredLeads = useMemo(() => {
    const leads = leadsResult?.leads ?? [];
    return leads
      .filter(lead => eventFilter === 'all' || lead.eventId === eventFilter)
      .filter(lead => stageFilter === 'all' || lead.stage === stageFilter)
      .sort((a, b) => (b.ref ?? 0) - (a.ref ?? 0));
  }, [leadsResult, eventFilter, stageFilter]);

  const summary = useMemo(() => computeReportSummary(filteredLeads), [filteredLeads]);

  const eventScopeLabel = eventFilter === 'all' ? 'All events' : (eventNameById.get(eventFilter) ?? 'Selected event');
  // useMemo with an empty deps array, not a bare call: the report is generated
  // once per view, and a bare `new Date()` in render is flagged as impure.
  const generatedAt = useMemo(
    () =>
      new Date().toLocaleString('en-IN', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
      }),
    []
  );

  return (
    <div className="print-color-exact mx-auto max-w-204 bg-background p-6 text-foreground print:p-0">
      <PrintHeader
        title="Tradeshow Lead Report"
        subtitle={`${eventScopeLabel} · Generated ${generatedAt} for follow-up · theparadise.co.in`}
      />

      <div className="mt-3.5">
        <ReportFilters
          eventFilter={eventFilter}
          onEventFilterChange={setEventFilter}
          stageFilter={stageFilter}
          onStageFilterChange={setStageFilter}
        />
      </div>

      {leadsResult && leadsResult.status !== 'ok' && (
        <p className="mt-4 text-sm text-muted-foreground print:hidden">{STATUS_MESSAGE[leadsResult.status]}</p>
      )}

      {!isPending && (!leadsResult || leadsResult.status === 'ok') && (
        <>
          <ReportSummaryRow summary={summary} />

          {filteredLeads.length === 0 ? (
            <Empty className="flex-none py-8">
              <EmptyHeader>
                <EmptyDescription>No leads match this filter.</EmptyDescription>
              </EmptyHeader>
            </Empty>
          ) : (
            <div>
              {filteredLeads.map(lead => (
                <LeadReportBlock
                  key={lead.id}
                  lead={lead}
                  eventName={eventNameById.get(lead.eventId)}
                  measures={measureByProductId}
                  itemNos={itemNoByProductId}
                />
              ))}
            </div>
          )}

          <PrintFooter right="Prices in INR" />
        </>
      )}
    </div>
  );
}
